import { RubricType } from '../types'
import type { FirestoreAssignmentRubric } from '../firestore/AssignmentRubric/schema'

/**
 * Canonical default score→percentage mappings, keyed by rubric score. These are
 * the mappings an assignment gets when the instructor does not customize
 * grading, and the values the custom-grading UI seeds its editor with. They are
 * persisted per assignment at creation (seedAssignmentRubricDefaults in
 * functions), so editing these constants only affects assignments created
 * afterwards — existing assignments keep the mappings stored on them.
 */
export const DQR_DEFAULT_SCORE_MAPPING: Readonly<Record<string, number>> = {
  '4': 100,
  '3': 75,
  '2': 50,
  '1': 25,
  '0': 0,
}

// Blooms gives full credit for any non-zero score, matching the legacy
// collapse-to-binary behaviour.
export const BLOOMS_DEFAULT_SCORE_MAPPING: Readonly<Record<string, number>> = {
  '6': 100,
  '5': 100,
  '4': 100,
  '3': 100,
  '2': 100,
  '1': 100,
  '0': 0,
}

export const PASS_FAIL_DEFAULT_SCORE_MAPPING: Readonly<Record<string, number>> =
  {
    '1': 100,
    '0': 0,
  }

/**
 * The default score mapping for a rubric type, or undefined when the type is
 * not eligible for score mapping (demoDelivery — internal demo-evaluation
 * rubrics stay out of instructor grading).
 */
export function defaultScoreMappingForRubricType(
  rubricType: RubricType
): Readonly<Record<string, number>> | undefined {
  switch (rubricType) {
    case RubricType.evidence:
    case RubricType.peerEngagement:
    case RubricType.criticalThinking:
    case RubricType.decisionMaking:
      return DQR_DEFAULT_SCORE_MAPPING
    case RubricType.blooms:
      return BLOOMS_DEFAULT_SCORE_MAPPING
    case RubricType.passFail:
      return PASS_FAIL_DEFAULT_SCORE_MAPPING
    case RubricType.demoDelivery:
      return undefined
    default: {
      // A new RubricType must be handled above (default mapping or an explicit
      // `return undefined` to opt it out of custom grading), else the build
      // fails here rather than the type silently dropping out of seeding.
      const exhaustive: never = rubricType
      return exhaustive
    }
  }
}

/** Distribute `total` across `n` buckets as integers that sum to `total`. */
export function equalSplit(total: number, n: number): number[] {
  if (n <= 0) return []
  const base = Math.floor(total / n)
  const remainder = total - base * n
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0))
}

/**
 * Role ids a rubric applies to, given the rubric's `assignmentRoles` and the
 * deck's current role ids. A non-empty `assignmentRoles` is role-specific; an
 * empty/undefined one applies to every role. The result is intersected with the
 * deck's current roles, and a role-specific rubric that references no current
 * role (stale data) falls back to all roles.
 */
export function applicableRoleIds(
  assignmentRoles: string[] | null | undefined,
  allRoleIds: string[]
): string[] {
  if (!assignmentRoles || assignmentRoles.length === 0) return allRoleIds
  const intersection = allRoleIds.filter((id) => assignmentRoles.includes(id))
  return intersection.length > 0 ? intersection : allRoleIds
}

/** The slice of a SlideRubric needed to build its default assignment config. */
export type DefaultRubricConfigInput = {
  id: string
  rubricType: RubricType
  assignmentRoles?: string[] | null
}

export type DefaultRubricConfig = {
  rubricId: string
  weights: FirestoreAssignmentRubric['weights']
  scoreMapping: Record<string, number>
}

/**
 * An instructor's saved score-mapping default for one rubric type: an entry of
 * the user's `score_mapping_defaults` document.
 */
export type SavedScoreMappingDefault = {
  rubricType: RubricType
  scoreMapping: Record<string, number>
}

/**
 * Whether a saved mapping can stand in for a canonical default: it must define
 * every score the canonical mapping does, with percentages in 0–100 that never
 * rise as the score falls. Anything else (a stale doc missing a level, a
 * malformed value) is rejected so the canonical default is pinned instead of a
 * mapping scoring could not apply.
 */
export function isUsableSavedScoreMapping(
  mapping: Record<string, number>,
  canonical: Readonly<Record<string, number>>
): boolean {
  const scores = Object.keys(canonical)
    .map(Number)
    .sort((a, b) => b - a)
  let previous = Number.POSITIVE_INFINITY
  for (const score of scores) {
    const value = mapping[String(score)]
    if (typeof value !== 'number' || !Number.isFinite(value)) return false
    if (value < 0 || value > 100 || value > previous) return false
    previous = value
  }
  return true
}

/**
 * The score mapping to pin on a rubric of `rubricType`: the instructor's saved
 * default for that type when usable, else the canonical default. Undefined for
 * types not eligible for score mapping.
 */
export function resolveDefaultScoreMapping(
  rubricType: RubricType,
  savedDefaults: readonly SavedScoreMappingDefault[] = []
): Readonly<Record<string, number>> | undefined {
  const canonical = defaultScoreMappingForRubricType(rubricType)
  if (canonical === undefined) return undefined
  const saved = savedDefaults.find(
    (entry) => entry.rubricType === rubricType
  )?.scoreMapping
  if (saved !== undefined && isUsableSavedScoreMapping(saved, canonical)) {
    // Only the canonical scores are kept: scoring never reads other keys, and
    // persisting them would carry dead data onto the assignment.
    return Object.fromEntries(
      Object.keys(canonical).map((score) => [score, saved[score]])
    )
  }
  return canonical
}

/**
 * Build the default per-assignment rubric configs for a deck: the default score
 * mapping per rubric type (the instructor's saved default when one is given,
 * else the canonical default), and weights that split 100% equally across the
 * rubrics applicable to each role (mirroring the equal weighting the legacy
 * fallback used). Rubric types with no canonical mapping (demoDelivery) are
 * excluded. Returns [] when the deck has no eligible rubrics.
 *
 * This must stay in lockstep with the custom-grading UI's seeding
 * (apps/web customRubricsConfig.ts), which delegates to the helpers above so
 * client and server produce identical defaults.
 */
export function buildDefaultAssignmentRubricConfigs({
  rubrics,
  roleIds,
  savedDefaults = [],
}: {
  rubrics: DefaultRubricConfigInput[]
  roleIds: string[]
  savedDefaults?: readonly SavedScoreMappingDefault[]
}): DefaultRubricConfig[] {
  const eligible = rubrics.filter(
    (rubric) =>
      defaultScoreMappingForRubricType(rubric.rubricType) !== undefined
  )
  if (eligible.length === 0) return []
  const mappingFor = (rubric: DefaultRubricConfigInput) => ({
    ...resolveDefaultScoreMapping(rubric.rubricType, savedDefaults)!,
  })

  if (roleIds.length === 0) {
    const split = equalSplit(100, eligible.length)
    return eligible.map((rubric, i) => ({
      rubricId: rubric.id,
      weights: { allRoles: split[i] },
      scoreMapping: mappingFor(rubric),
    }))
  }

  // Per role, split 100% across the rubrics applicable to that role;
  // non-applicable rubrics get no entry (byRole key presence encodes
  // applicability downstream).
  const weightByRubricAndRole = new Map<string, Record<string, number>>()
  for (const rubric of eligible) weightByRubricAndRole.set(rubric.id, {})
  for (const roleId of roleIds) {
    const applicable = eligible.filter((rubric) =>
      applicableRoleIds(rubric.assignmentRoles, roleIds).includes(roleId)
    )
    const split = equalSplit(100, applicable.length)
    applicable.forEach((rubric, i) => {
      weightByRubricAndRole.get(rubric.id)![roleId] = split[i]
    })
  }

  return eligible.map((rubric) => ({
    rubricId: rubric.id,
    weights: { byRole: weightByRubricAndRole.get(rubric.id)! },
    scoreMapping: mappingFor(rubric),
  }))
}
